import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Calculator, BookmarkSimple, PencilSimple, FloppyDisk, Star,
  CurrencyDollar, Package, UsersThree, PaperPlaneTilt, ArrowRight, Copy, ListBullets, FilePdf, MapPin, ArrowsClockwise
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import axiosInstance from '../api/api';
import SendQuoteModal from '../components/SendQuoteModal';
import LaborTab from '../components/ai-pricing/LaborTab';
import PricingTab from '../components/ai-pricing/PricingTab';
import { loadingSteps } from '../components/ai-pricing/constants';
import { AILoadingSequence } from '../components/ai-pricing/AILoadingSequence';
import { TierCard } from '../components/ai-pricing/TierCard';
import { MaterialsList } from '../components/ai-pricing/MaterialsList';
import { SaveEstimateModal } from '../components/ai-pricing/SaveEstimateModal';
import { SendProposalModal } from '../components/ai-pricing/SendProposalModal';
import { ChangeOrderModal } from '../components/ai-pricing/ChangeOrderModal';
import { SavedEstimatesSidebar } from '../components/ai-pricing/SavedEstimatesSidebar';
import { ProposalsTrackerSidebar } from '../components/ai-pricing/ProposalsTrackerSidebar';
import { CompareModal } from '../components/ai-pricing/CompareModal';
import { AuthCheckingScreen, AccessDeniedScreen } from '../components/ai-pricing/AccessGate';
import { EstimateForm, PHOTO_URL_BASE } from '../components/ai-pricing/EstimateForm';

export default function AIPricingTool() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [jobTypes, setJobTypes] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [showSendProposal, setShowSendProposal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSavedSidebar, setShowSavedSidebar] = useState(false);
  const [showProposalsSidebar, setShowProposalsSidebar] = useState(false);
  const [showChangeOrder, setShowChangeOrder] = useState(false);
  const [savedEstimates, setSavedEstimates] = useState([]);
  const [selectedTier, setSelectedTier] = useState('standard');
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [activeTab, setActiveTab] = useState('pricing');
  const [isEditing, setIsEditing] = useState(false);
  const [editedEstimate, setEditedEstimate] = useState(null);
  const [currentSavedEstimateId, setCurrentSavedEstimateId] = useState(null);
  const [profitMargin, setProfitMargin] = useState(0);
  const [showProfitCalculator, setShowProfitCalculator] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [scopeItems, setScopeItems] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const loadingIntervalRef = useRef(null);

  const [formData, setFormData] = useState({
    job_type: '',
    location: '',
    description: '',
    square_footage: '',
    timeline: '',
    materials_included: true
  });

  useEffect(() => {
    fetchJobTypes();
    fetchSavedEstimates();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        setIsAuthorized(user.user_type === 'contractor');
      } catch (error) {
        console.error('Error checking authorization:', error);
        setIsAuthorized(false);
      }
    }
    setAuthChecking(false);
  }, []);

  useEffect(() => {
    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
    };
  }, []);

  const fetchJobTypes = async () => {
    try {
      const response = await axiosInstance.get('/ai-pricing/job-types');
      if (response.data.success) {
        setJobTypes(response.data.job_types);
      }
    } catch (error) {
      console.error('Error fetching job types:', error);
    }
  };

  const fetchSavedEstimates = async () => {
    try {
      const response = await axiosInstance.get('/ai-pricing/saved');
      if (response.data.success) {
        setSavedEstimates(response.data.saved_estimates);
      }
    } catch (error) {
      console.error('Error fetching saved estimates:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      job_type: template.job_type,
      description: template.description,
      square_footage: template.square_footage,
      timeline: template.timeline
    }));
  };

  const startLoadingSequence = () => {
    setLoadingStep(0);
    setLoadingProgress(0);

    let progress = 0;
    const totalDuration = loadingSteps.reduce((sum, s) => sum + s.duration, 0);
    const incrementPerTick = 100 / (totalDuration / 100);

    loadingIntervalRef.current = setInterval(() => {
      progress += incrementPerTick;
      setLoadingProgress(Math.min(progress, 99));

      let elapsed = 0;
      for (let i = 0; i < loadingSteps.length; i++) {
        elapsed += loadingSteps[i].duration;
        if ((progress / 100) * totalDuration < elapsed) {
          setLoadingStep(i);
          break;
        }
      }
    }, 100);
  };

  const stopLoadingSequence = () => {
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
      loadingIntervalRef.current = null;
    }
    setLoadingProgress(100);
    setLoadingStep(loadingSteps.length);
  };

  const combinedDescription = () => {
    const base = (formData.description || '').trim();
    if (scopeItems.length === 0) return base;
    return `${base}${base ? '\n\n' : ''}Scope checklist:\n${scopeItems.map((s) => `- ${s}`).join('\n')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.job_type) {
      toast({ title: 'Missing Information', description: 'Please select a job type', variant: 'destructive' });
      return;
    }

    if (combinedDescription().length < 20) {
      toast({ title: 'Missing Information', description: 'Please provide a detailed description (at least 20 characters) or dictate a scope checklist', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setEstimate(null);
    setActiveTab('pricing');
    startLoadingSequence();

    try {
      const payload = {
        ...formData,
        description: combinedDescription(),
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        photos: photos.map(p => p.file_id),
      };

      const response = await axiosInstance.post('/ai-pricing/estimate', payload);

      if (response.data.success) {
        stopLoadingSequence();
        setEstimate(response.data.estimate);
        toast({ title: 'Estimate Generated', description: 'Your AI pricing estimate is ready!' });
      }
    } catch (error) {
      stopLoadingSequence();
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to generate estimate', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const saveEstimate = async (name) => {
    try {
      const response = await axiosInstance.post('/ai-pricing/saved', {
        name,
        job_type: formData.job_type,
        location: formData.location,
        description: combinedDescription(),
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        materials_included: formData.materials_included,
        estimate_data: estimate,
        photos: photos.map(p => p.file_id),
      });

      if (response.data.success) {
        toast({ title: 'Saved!', description: response.data.message });
        fetchSavedEstimates();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save estimate', variant: 'destructive' });
    }
  };

  const deleteEstimate = async (id) => {
    try {
      await axiosInstance.delete(`/ai-pricing/saved/${id}`);
      toast({ title: 'Deleted' });
      fetchSavedEstimates();
      setSelectedForCompare(prev => prev.filter(i => i !== id));
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const loadSavedEstimate = async (est) => {
    try {
      const response = await axiosInstance.get(`/ai-pricing/saved/${est.id}`);
      if (response.data.success) {
        const loaded = response.data.estimate;
        setEstimate(loaded.estimate_data);
        setCurrentSavedEstimateId(est.id);
        setFormData({
          job_type: loaded.job_type,
          location: loaded.location,
          description: loaded.description,
          square_footage: loaded.square_footage || '',
          timeline: '',
          materials_included: loaded.materials_included
        });
        const savedPhotoIds = loaded.photos || [];
        setPhotos(savedPhotoIds.map(file_id => ({
          file_id,
          url: `${PHOTO_URL_BASE}${file_id}`,
          name: file_id,
          size: 0,
        })));
        setShowSavedSidebar(false);
        setIsEditing(false);
        setEditedEstimate(null);
        toast({ title: 'Estimate Loaded', description: `Loaded "${est.name}"` });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load estimate', variant: 'destructive' });
    }
  };

  const runComparison = async () => {
    if (selectedForCompare.length < 2) {
      toast({ title: 'Select at least 2 estimates', variant: 'destructive' });
      return;
    }

    try {
      const response = await axiosInstance.post('/ai-pricing/compare', selectedForCompare);
      if (response.data.success) {
        setComparisonData(response.data);
        setShowCompareModal(true);
        setShowSavedSidebar(false);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to compare estimates', variant: 'destructive' });
    }
  };

  const startEditing = () => {
    setEditedEstimate(JSON.parse(JSON.stringify(estimate)));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setEditedEstimate(null);
    setIsEditing(false);
  };

  const saveEdits = async () => {
    setEstimate(editedEstimate);

    if (editedEstimate.tiers) {
      editedEstimate.price_range = {
        low: editedEstimate.tiers.economy?.total || 0,
        mid: editedEstimate.tiers.standard?.total || 0,
        high: editedEstimate.tiers.premium?.total || 0
      };
    }

    if (currentSavedEstimateId) {
      try {
        await axiosInstance.put(`/ai-pricing/saved/${currentSavedEstimateId}`, {
          estimate_data: editedEstimate
        });
        toast({ title: 'Saved!', description: 'Estimate updated successfully' });
        fetchSavedEstimates();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to save changes', variant: 'destructive' });
      }
    } else {
      toast({ title: 'Changes Applied', description: 'Save the estimate to keep these changes' });
    }

    setIsEditing(false);
  };

  const handleTierChange = (tierKey, updatedTier) => {
    setEditedEstimate(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tierKey]: updatedTier
      },
      price_range: {
        ...prev.price_range,
        [tierKey === 'economy' ? 'low' : tierKey === 'premium' ? 'high' : 'mid']: updatedTier.total
      }
    }));
  };

  const handleMaterialsChange = (updatedMaterials) => {
    setEditedEstimate(prev => ({
      ...prev,
      materials_list: updatedMaterials
    }));
  };

  const handleLaborChange = (field, value) => {
    setEditedEstimate(prev => ({
      ...prev,
      labor_breakdown: {
        ...prev.labor_breakdown,
        [field]: value
      }
    }));
  };

  const applyProfitMargin = (price) => {
    if (!price || profitMargin === 0) return price;
    return Math.round(price * (1 + profitMargin / 100));
  };

  const calculateProfit = (price) => {
    if (!price || profitMargin === 0) return 0;
    return Math.round(price * (profitMargin / 100));
  };

  const displayEstimate = isEditing ? editedEstimate : estimate;

  const copyEstimate = () => {
    if (!estimate) return;
    const tier = estimate.tiers?.[selectedTier] || {};
    const text = `AI PRICING ESTIMATE - ${selectedTier.toUpperCase()} TIER
==================
Job Type: ${formData.job_type.replace('_', ' ').toUpperCase()}
Location: ${formData.location || 'Not specified'}

PRICE: $${tier.total?.toLocaleString() || estimate.price_range?.mid?.toLocaleString() || 0}
TIMELINE: ${tier.timeline_days || estimate.timeline_estimate?.min_days}-${estimate.timeline_estimate?.max_days} days

Generated by ConstructHelp AI Pricing Tool`;

    navigator.clipboard.writeText(text);
    toast({ title: 'Copied!', description: 'Estimate copied to clipboard' });
  };

  const downloadPdf = async () => {
    if (!estimate) return;
    setDownloadingPdf(true);
    try {
      const response = await axiosInstance.post('/ai-pricing/estimate/pdf', {
        estimate,
        job_type: formData.job_type,
        location: formData.location,
        description: formData.description,
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        selected_tier: selectedTier,
        profit_margin: profitMargin,
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `estimate_${formData.job_type || 'job'}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'PDF Downloaded', description: 'Your branded estimate is ready to share' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to generate PDF', variant: 'destructive' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const useInJobPost = () => {
    sessionStorage.setItem('ai_pricing_estimate', JSON.stringify({
      job_type: formData.job_type,
      description: formData.description,
      budget_min: estimate.price_range.low,
      budget_max: estimate.price_range.high,
      location: formData.location
    }));
    navigate('/post-job');
  };

  if (authChecking) {
    return <AuthCheckingScreen />;
  }

  if (!isAuthorized) {
    return <AccessDeniedScreen currentUser={currentUser} />;
  }

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="ai-pricing-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] dark:bg-[#0F172A] border-b-2 border-black dark:border-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-white/80 dark:text-neutral-400 hover:text-white dark:hover:text-white font-medium transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft size={20} weight="bold" />
              </button>
              <div className="w-12 h-12 bg-white/20 dark:bg-[#2B4C6F]/20 border-2 border-white/30 dark:border-[#2B4C6F] flex items-center justify-center">
                <Calculator size={24} weight="bold" className="text-white dark:text-[#2B4C6F]" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-black text-white dark:text-white uppercase tracking-tight">
                  AI JOB PRICING
                </h1>
                <p className="text-white/70 dark:text-neutral-400 text-xs uppercase tracking-widest">
                  MATERIALS • TIERS • ESTIMATES
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setShowProposalsSidebar(true)}
                variant="outline"
                className="h-10 px-4 bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 rounded-none font-bold uppercase tracking-wider text-xs"
                data-testid="open-proposals-btn"
              >
                <PaperPlaneTilt size={18} weight="fill" className="mr-2" />
                Proposals
              </Button>
              <Button
                onClick={() => setShowSavedSidebar(true)}
                variant="outline"
                className="h-10 px-4 bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 rounded-none font-bold uppercase tracking-wider text-xs"
                data-testid="open-saved-btn"
              >
                <BookmarkSimple size={18} weight="fill" className="mr-2" />
                Saved ({savedEstimates.length})
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          
          {/* LEFT: Form Panel */}
          <div className="lg:col-span-4">
            <EstimateForm
              formData={formData}
              onChange={handleChange}
              onSubmit={handleSubmit}
              loading={loading}
              jobTypes={jobTypes}
              onApplyTemplate={applyTemplate}
              photos={photos}
              setPhotos={setPhotos}
              scopeItems={scopeItems}
              setScopeItems={setScopeItems}
            />
          </div>

          {/* RIGHT: Results Panel */}
          <div className="lg:col-span-8">
            {/* Empty State */}
            {!estimate && !loading && (
              <div className="bg-white dark:bg-[#0F172A] border-2 border-dashed border-neutral-400 dark:border-neutral-600 p-12 flex flex-col items-center justify-center min-h-[500px]">
                <Calculator size={80} weight="light" className="text-neutral-300 dark:text-neutral-700 mb-6" />
                <h3 className="text-xl font-black text-neutral-900 dark:text-white uppercase tracking-wide mb-2">
                  YOUR ESTIMATE WILL APPEAR HERE
                </h3>
                <p className="text-neutral-500 text-center max-w-md">
                  Fill in the job details on the left and click "Generate Estimate" to get an AI-powered price breakdown with materials list and tiered options.
                </p>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="min-h-[500px] flex flex-col">
                <AILoadingSequence currentStep={loadingStep} progress={loadingProgress} />
              </div>
            )}

            {/* Results */}
            {estimate && !loading && (
              <div className="space-y-6">
                {/* Regional Calibration Banner */}
                {displayEstimate.regional_calibration && (
                  <div
                    className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white px-4 py-3 flex items-center gap-3 shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]"
                    data-testid="regional-calibration-banner"
                  >
                    <div className="w-9 h-9 bg-[#2B4C6F] flex items-center justify-center shrink-0">
                      <MapPin size={18} weight="fill" className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-xs">
                        Locally Calibrated — <span className="capitalize">{displayEstimate.regional_calibration.area}</span>
                      </p>
                      <p className="text-xs text-neutral-500 font-mono">
                        Cost index {displayEstimate.regional_calibration.index}{' '}
                        <span className={displayEstimate.regional_calibration.pct_vs_national > 0 ? 'text-red-600 dark:text-red-400 font-bold' : displayEstimate.regional_calibration.pct_vs_national < 0 ? 'text-green-700 dark:text-green-400 font-bold' : 'font-bold'}>
                          ({displayEstimate.regional_calibration.pct_vs_national > 0 ? '+' : ''}{displayEstimate.regional_calibration.pct_vs_national}% vs national avg)
                        </span>
                        {' '}· labor idx {displayEstimate.regional_calibration.labor_index} · materials idx {displayEstimate.regional_calibration.material_index}
                      </p>
                    </div>
                  </div>
                )}

                {/* Edit Mode Banner */}
                {isEditing && (
                  <div className="bg-amber-500 text-white p-4 border-2 border-black flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <PencilSimple size={20} weight="bold" />
                      <span className="font-bold uppercase tracking-wide">Edit Mode</span>
                      <span className="text-amber-100 text-sm">- Modify prices, materials, and labor</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={cancelEditing}
                        size="sm"
                        variant="outline"
                        className="h-8 px-4 bg-white/20 hover:bg-white/30 text-white border-white font-bold rounded-none"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={saveEdits}
                        size="sm"
                        className="h-8 px-4 bg-white text-amber-600 hover:bg-amber-50 font-bold border-2 border-black rounded-none shadow-[2px_2px_0px_#000]"
                        data-testid="save-edits-btn"
                      >
                        <FloppyDisk size={16} className="mr-1" /> Save Changes
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tier Selection */}
                {displayEstimate.tiers && (
                  <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]">
                    <div className="p-4 border-b-2 border-black dark:border-white bg-neutral-50 dark:bg-neutral-900 flex items-center justify-between">
                      <div>
                        <h4 className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
                          <Star size={18} weight="fill" className="text-amber-500" />
                          CHOOSE YOUR TIER
                          {isEditing && <Badge className="bg-amber-500 text-white text-[10px] ml-2">EDITING</Badge>}
                        </h4>
                        <p className="text-xs text-neutral-500 mt-1">
                          {isEditing ? 'Click on prices to edit them' : 'Select a pricing tier to see detailed breakdown'}
                        </p>
                      </div>
                      {!isEditing && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowSendProposal(true)}
                            size="sm"
                            className="h-8 px-3 font-bold text-xs bg-[#D95800] hover:bg-[#B84A00] text-white border-2 border-black rounded-none shadow-[2px_2px_0px_#000]"
                            data-testid="send-to-client-btn"
                          >
                            <PaperPlaneTilt size={14} className="mr-1" /> Send to Client
                          </Button>
                          <Button
                            onClick={() => setShowChangeOrder(true)}
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 font-bold text-xs border-2 border-black rounded-none shadow-[2px_2px_0px_#000]"
                            data-testid="change-order-btn"
                          >
                            <ArrowsClockwise size={14} className="mr-1" /> Change Order
                          </Button>
                          <Button
                            onClick={startEditing}
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 font-bold text-xs border-2 border-black rounded-none shadow-[2px_2px_0px_#000]"
                            data-testid="edit-estimate-btn"
                          >
                            <PencilSimple size={14} className="mr-1" /> Edit Prices
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="grid md:grid-cols-3 gap-0 divide-x-0 md:divide-x-2 divide-y-2 md:divide-y-0 divide-black dark:divide-white">
                      {['economy', 'standard', 'premium'].map((tierKey) => (
                        <TierCard
                          key={tierKey}
                          tier={displayEstimate.tiers[tierKey]}
                          tierKey={tierKey}
                          isSelected={selectedTier === tierKey}
                          onSelect={setSelectedTier}
                          isEditing={isEditing}
                          onTierChange={handleTierChange}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Tabs */}
                <div className="flex border-2 border-black dark:border-white bg-white dark:bg-[#0F172A]">
                  {[
                    { id: 'pricing', label: 'Pricing', icon: CurrencyDollar },
                    { id: 'materials', label: 'Materials', icon: Package },
                    { id: 'labor', label: 'Labor', icon: UsersThree }
                  ].map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex items-center justify-center gap-2 p-4 font-bold uppercase tracking-wider text-sm transition-colors ${
                          activeTab === tab.id 
                            ? 'bg-[#2B4C6F] text-white' 
                            : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                        }`}
                        data-testid={`tab-${tab.id}`}
                      >
                        <Icon size={18} weight={activeTab === tab.id ? 'fill' : 'bold'} />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {activeTab === 'pricing' && (
                  <PricingTab
                    displayEstimate={displayEstimate}
                    selectedTier={selectedTier}
                    showProfitCalculator={showProfitCalculator}
                    setShowProfitCalculator={setShowProfitCalculator}
                    profitMargin={profitMargin}
                    setProfitMargin={setProfitMargin}
                    applyProfitMargin={applyProfitMargin}
                    calculateProfit={calculateProfit}
                    setShowQuoteModal={setShowQuoteModal}
                    photos={photos}
                  />
                )}

                {activeTab === 'materials' && (
                  <MaterialsList 
                    materials={displayEstimate.materials_list} 
                    selectedTier={selectedTier}
                    isEditing={isEditing}
                    onMaterialsChange={handleMaterialsChange}
                  />
                )}

                {activeTab === 'labor' && (
                  <LaborTab
                    displayEstimate={displayEstimate}
                    selectedTier={selectedTier}
                    isEditing={isEditing}
                    handleLaborChange={handleLaborChange}
                  />
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={() => setShowQuoteModal(true)}
                    className="flex-1 min-w-[200px] h-14 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase tracking-wider border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F] hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_0px_#000000] transition-all rounded-none"
                    data-testid="send-quote-btn"
                  >
                    <PaperPlaneTilt size={20} weight="fill" className="mr-2" />
                    SEND QUOTE
                  </Button>
                  <Button
                    onClick={() => setShowSaveModal(true)}
                    variant="outline"
                    className="h-14 px-6 font-black uppercase tracking-wider border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] text-neutral-900 dark:text-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F] hover:-translate-y-1 hover:-translate-x-1 transition-all rounded-none"
                    data-testid="save-estimate-btn"
                  >
                    <BookmarkSimple size={20} weight="fill" className="mr-2" />
                    SAVE
                  </Button>
                  <Button
                    onClick={useInJobPost}
                    variant="outline"
                    className="h-14 px-6 font-black uppercase tracking-wider border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] text-neutral-900 dark:text-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F] hover:-translate-y-1 hover:-translate-x-1 transition-all rounded-none"
                    data-testid="use-in-job-btn"
                  >
                    USE IN JOB
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                  <Button
                    onClick={downloadPdf}
                    disabled={downloadingPdf}
                    variant="outline"
                    className="h-14 px-6 font-black uppercase tracking-wider border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] text-neutral-900 dark:text-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F] hover:-translate-y-1 hover:-translate-x-1 transition-all rounded-none disabled:opacity-50"
                    data-testid="download-pdf-btn"
                  >
                    <FilePdf size={20} weight="fill" className="mr-2 text-red-600" />
                    {downloadingPdf ? 'GENERATING...' : 'PDF'}
                  </Button>
                  <Button
                    onClick={copyEstimate}
                    variant="outline"
                    className="h-14 px-6 font-black uppercase tracking-wider border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] text-neutral-900 dark:text-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F] hover:-translate-y-1 hover:-translate-x-1 transition-all rounded-none"
                    data-testid="copy-btn"
                  >
                    <Copy size={18} />
                  </Button>
                </div>

                {/* View Quotes Link */}
                <div className="flex justify-center pt-2">
                  <button
                    onClick={() => navigate('/quotes')}
                    className="flex items-center gap-2 text-sm font-bold text-neutral-500 hover:text-[#2B4C6F] uppercase tracking-wider transition-colors"
                    data-testid="view-quotes-link"
                  >
                    <ListBullets size={16} weight="bold" />
                    VIEW ALL SENT QUOTES
                  </button>
                </div>

                {/* Disclaimer */}
                <p className="text-xs text-neutral-500 text-center uppercase tracking-wider">
                  This estimate is generated using AI. Actual costs may vary.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <SendQuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        estimate={estimate}
        formData={formData}
        adjustedPrices={profitMargin > 0 ? {
          low: applyProfitMargin(estimate?.price_range?.low || 0),
          mid: applyProfitMargin(estimate?.price_range?.mid || 0),
          high: applyProfitMargin(estimate?.price_range?.high || 0)
        } : null}
        profitMargin={profitMargin}
      />
      
      {showSendProposal && displayEstimate && (
        <SendProposalModal
          onClose={() => setShowSendProposal(false)}
          estimate={displayEstimate}
          formData={formData}
        />
      )}
      <SaveEstimateModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={saveEstimate}
        formData={formData}
      />
      
      <ProposalsTrackerSidebar
        isOpen={showProposalsSidebar}
        onClose={() => setShowProposalsSidebar(false)}
      />

      <ChangeOrderModal
        isOpen={showChangeOrder}
        onClose={() => setShowChangeOrder(false)}
        context={displayEstimate ? {
          job_type: formData.job_type,
          location: formData.location,
          original_total: displayEstimate.tiers?.[selectedTier]?.total ?? null,
          original_scope: formData.description,
          source: 'estimate',
        } : null}
      />

      <SavedEstimatesSidebar
        isOpen={showSavedSidebar}
        onClose={() => setShowSavedSidebar(false)}
        savedEstimates={savedEstimates}
        onLoad={loadSavedEstimate}
        onDelete={deleteEstimate}
        onCompare={(ids) => typeof ids === 'object' ? setSelectedForCompare(ids) : runComparison()}
        selectedForCompare={selectedForCompare}
      />
      
      <CompareModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        comparison={comparisonData}
      />
    </div>
  );
}
